import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import apiClient from '@/lib/api/client';
import { LargeTitleHeader } from '@/components/common';
import { labourEstimatePath } from '@/lib/utils/navPaths';

type LabourEstimate = {
  id: number;
  title: string;
  client_id: number;
  job_id: number;
  total_hours: number;
  hourly_rate: number;
  total_amount: number;
  status: string;
  notes?: string;
  created_at: string;
  job_title?: string;
  client_name?: string;
  crew_name?: string;
};

export const LabourEstimateDetailPage = () => {
  const { id, clientId, jobId } = useParams<{ id: string; clientId?: string; jobId?: string }>();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState<LabourEstimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    total_hours: 0,
    hourly_rate: 0,
    status: '',
    notes: '',
  });

  useEffect(() => {
    if (id) {
      loadEstimate();
    }
  }, [id]);

  // Upgrade the flat /labour-estimates/:id URL to the canonical /clients/:c/jobs/:j/labour-estimates/:e
  // once the estimate (and thus its client/job) is known, so the address bar mirrors the hierarchy.
  useEffect(() => {
    if (!id || !estimate?.client_id || !estimate.job_id) return;
    if (String(clientId) !== String(estimate.client_id) || String(jobId) !== String(estimate.job_id)) {
      navigate(labourEstimatePath(estimate.client_id, estimate.job_id, id), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate, clientId, jobId, id]);

  const loadEstimate = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/labour-estimates/${id}`);
      setEstimate(response.data?.data || response.data);
    } catch (err) {
      console.error('Failed to load labour estimate:', err);
      setError('Failed to load labour estimate');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    if (estimate) {
      setEditForm({
        title: estimate.title,
        total_hours: estimate.total_hours,
        hourly_rate: estimate.hourly_rate,
        status: estimate.status,
        notes: estimate.notes || '',
      });
      setIsEditing(true);
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const saveChanges = async () => {
    if (!estimate) return;
    
    try {
      setSaving(true);
      await apiClient.put(`/labour-estimates/${id}`, {
        title: editForm.title,
        total_hours: editForm.total_hours,
        hourly_rate: editForm.hourly_rate,
        total_amount: editForm.total_hours * editForm.hourly_rate,
        status: editForm.status,
        notes: editForm.notes,
      });
      await loadEstimate();
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save labour estimate:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this labour estimate? This action cannot be undone.')) {
      return;
    }
    
    try {
      setDeleting(true);
      await apiClient.delete(`/labour-estimates/${id}`);
      navigate(-1);
    } catch (err) {
      console.error('Failed to delete labour estimate:', err);
      setError('Failed to delete labour estimate');
      setDeleting(false);
    }
  };

  const calculateTotal = () => {
    return editForm.total_hours * editForm.hourly_rate;
  };

  // DECIMALs arrive as strings from the API — coerce or toLocaleString is the
  // String prototype's and the options are ignored (#96)
  const formatCurrency = (value: number | string) =>
    `$${(Number(value) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-2 text-gray-600">Loading labour estimate...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !estimate) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || 'Labour estimate not found'}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90"
            >
              Go Back
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col min-h-screen">
        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title={`LAB-${estimate.id.toString().padStart(5, '0')}`}
          subtitle={estimate.title}
          onBack={() => {
            if (estimate.client_id && estimate.job_id) navigate(`/clients/${estimate.client_id}/jobs/${estimate.job_id}`);
            else navigate(-1);
          }}
          backLabel="Job"
          actions={
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button onClick={cancelEditing} disabled={saving} className="h-9 px-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                  <button onClick={saveChanges} disabled={saving} className="h-9 px-3 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
                </>
              ) : (
                <>
                  <span className={`hidden sm:inline px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(estimate.status)}`}>
                    {estimate.status?.charAt(0).toUpperCase() + estimate.status?.slice(1)}
                  </span>
                  <button onClick={() => window.print()} className="hidden sm:flex items-center gap-1.5 h-9 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                    <span className="material-symbols-outlined text-base">print</span>
                    Print
                  </button>
                  <button onClick={startEditing} className="flex items-center gap-1.5 h-9 px-3 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90">
                    <span className="material-symbols-outlined text-base">edit</span>
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                  <button onClick={handleDelete} disabled={deleting} className="flex items-center gap-1.5 h-9 px-3 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                    <span className="material-symbols-outlined text-base">delete</span>
                    <span className="hidden sm:inline">{deleting ? 'Deleting…' : 'Delete'}</span>
                  </button>
                </>
              )}
            </div>
          }
        />

        <div className="p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            {/* Summary Card */}
            <div className="bg-white rounded-xl border border-gray-200 mb-6">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Estimate Summary</h3>
              </div>
              <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-600">Estimate Number</p>
                  <p className="font-semibold text-gray-900">LAB-{estimate.id.toString().padStart(5, '0')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Created Date</p>
                  <p className="font-semibold text-gray-900">{formatDate(estimate.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Job</p>
                  <p className="font-semibold text-gray-900">{estimate.job_title || `Job #${estimate.job_id}`}</p>
                </div>
                {estimate.crew_name && (
                  <div>
                    <p className="text-sm text-gray-600">Crew</p>
                    <p className="font-semibold text-gray-900">{estimate.crew_name}</p>
                  </div>
                )}
              </div>
              {isEditing && (
                <div className="p-6 border-t border-gray-200 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="approved">Approved</option>
                      <option value="declined">Declined</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Labour Details Card */}
            <div className="bg-white rounded-xl border border-gray-200 mb-6">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Labour Details</h3>
              </div>
              <div className="p-6">
                {isEditing ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Total Hours</label>
                      <input
                        type="number"
                        step="0.5"
                        value={editForm.total_hours}
                        onChange={(e) => setEditForm({ ...editForm, total_hours: Number(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-center text-xl font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.hourly_rate}
                        onChange={(e) => setEditForm({ ...editForm, hourly_rate: Number(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-center text-xl font-bold"
                      />
                    </div>
                    <div className="bg-primary/10 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                      <p className="text-3xl font-bold text-primary">{formatCurrency(calculateTotal())}</p>
                      <p className="text-sm text-gray-500">{editForm.total_hours} hrs × {formatCurrency(editForm.hourly_rate)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-600 mb-1">Total Hours</p>
                      <p className="text-3xl font-bold text-gray-900">{estimate.total_hours}</p>
                      <p className="text-sm text-gray-500">hours</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-600 mb-1">Hourly Rate</p>
                      <p className="text-3xl font-bold text-gray-900">{formatCurrency(estimate.hourly_rate)}</p>
                      <p className="text-sm text-gray-500">per hour</p>
                    </div>
                    <div className="bg-primary/10 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                      <p className="text-3xl font-bold text-primary">{formatCurrency(estimate.total_amount)}</p>
                      <p className="text-sm text-gray-500">{estimate.total_hours} hrs × {formatCurrency(estimate.hourly_rate)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes Card */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Notes</h3>
              </div>
              <div className="p-6">
                {isEditing ? (
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Add notes about this labour estimate..."
                  />
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {estimate.notes || 'No notes'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
